import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaUsersCog, FaHandshake, FaGraduationCap, FaChartLine, FaCalendarAlt, FaUserPlus, FaArrowLeft } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../services/api';
import './AdminDashboard.css';
import { ROLES } from '../../config/constants';

function AdminDashboard() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const userIsSuperAdmin = currentUser?.role === ROLES.SUPER_ADMIN;
    const userIsAdmin = currentUser?.role === ROLES.ADMIN;
    const userIsEditor = currentUser?.role === ROLES.EDITOR;
    // Editor role can access more parts of the dashboard but still limited compared to admin roles
    const canAccessAll = userIsSuperAdmin || userIsAdmin;
    // Editors can access applications and certain management features
    const canAccessApplications = canAccessAll || userIsEditor;
    
    const [stats, setStats] = useState({
        totalMembers: 0,
        partnershipApplications: 0,
        academyApplications: 0,
        totalUsers: 0
    });
    const [activities, setActivities] = useState([]);
    const [applications, setApplications] = useState({
        academy: [],
        partnership: []
    });
    const [statusCounts, setStatusCounts] = useState({
        academy: { pending: 0, approved: 0, rejected: 0, waitlist: 0 },
        partnership: { pending: 0, approved: 0, rejected: 0, 'in-review': 0 }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAdminStats = async () => {
            try {
                setLoading(true);
                const adminStatsData = await adminAPI.getStats();
                
                // Update stats from counters
                setStats({
                    totalMembers: adminStatsData.counters.totalMembers || 0,
                    partnershipApplications: adminStatsData.counters.partnershipApplications || 0,
                    academyApplications: adminStatsData.counters.academyApplications || 0,
                    totalUsers: adminStatsData.counters.totalUsers || 0
                });
                
                // Update activities
                setActivities(adminStatsData.recentActivity || []);
                
                // Update applications
                setApplications({
                    academy: adminStatsData.recentAcademyApplications || [],
                    partnership: adminStatsData.recentPartnershipApplications || []
                });
                
                // Update status counts
                setStatusCounts({
                    academy: adminStatsData.academyStatusCounts || { pending: 0, approved: 0, rejected: 0, waitlist: 0 },
                    partnership: adminStatsData.partnershipStatusCounts || { pending: 0, approved: 0, rejected: 0, 'in-review': 0 }
                });
                
                setLoading(false);
            } catch (error) {
                setError(error.message || 'Failed to load admin statistics');
                setLoading(false);
            }
        };
        
        fetchAdminStats();
    }, []);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'Unknown time';
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours < 24) {
            return `${hours} hours ago`;
        }
        return date.toLocaleDateString();
    };

    if (loading) {
        return <div className="loading-container">Loading admin dashboard...</div>;
    }

    if (error) {
        return (
            <div className="admin-dashboard">
                <div className="error-message">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <button className="back-button" onClick={() => navigate(-1)}>
                    <FaArrowLeft /> Go Back
                </button>
                <h1>Admin Dashboard</h1>
                <p className="admin-subtitle">
                    {userIsEditor 
                        ? "Welcome to the editor dashboard. You can manage applications and content."
                        : "Welcome to your administrative control center"
                    }
                </p>
            </div>

            <div className="admin-stats-grid">
                {/* Only show all stats to admins/super admins */}
                {canAccessAll && (
                    <>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <FaUsers />
                            </div>
                            <div className="stat-content">
                                <h3>Total Members</h3>
                                <p className="stat-value">{stats.totalMembers}</p>
                            </div>
                        </div>
                    </>
                )}
                
                {/* Show Partnership stats to admins and editors */}
                {canAccessApplications && (
                    <div className="stat-card">
                        <div className="stat-icon">
                            <FaHandshake />
                        </div>
                        <div className="stat-content">
                            <h3>Partnership Applications</h3>
                            <p className="stat-value">{stats.partnershipApplications}</p>
                            <div className="stat-breakdown">
                                <span>Pending: {statusCounts.partnership.pending}</span>
                                <span>Approved: {statusCounts.partnership.approved}</span>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Show Academy stats to everyone with access */}
                {canAccessApplications && (
                    <div className="stat-card">
                        <div className="stat-icon">
                            <FaGraduationCap />
                        </div>
                        <div className="stat-content">
                            <h3>Academy Applications</h3>
                            <p className="stat-value">{stats.academyApplications}</p>
                            <div className="stat-breakdown">
                                <span>Pending: {statusCounts.academy.pending}</span>
                                <span>Approved: {statusCounts.academy.approved}</span>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Only show user stats to admins/super admins */}
                {canAccessAll && (
                    <div className="stat-card">
                        <div className="stat-icon">
                            <FaUserPlus />
                        </div>
                        <div className="stat-content">
                            <h3>Total Users</h3>
                            <p className="stat-value">{stats.totalUsers}</p>
                        </div>
                    </div>
                )}
            </div>

            <div className="admin-actions-grid">
                {/* Only show Members Management to admins/super admins */}
                {canAccessAll && (
                    <div className="action-card" onClick={() => navigate('/admin/members')}>
                        <FaUsers className="action-icon" />
                        <h3>Members Management</h3>
                        <p>Manage member profiles, roles, and permissions</p>
                    </div>
                )}
                
                {/* Only show Users & Permissions to super admins */}
                {userIsSuperAdmin && (
                    <div className="action-card" onClick={() => navigate('/admin/users')}>
                        <FaUsersCog className="action-icon" />
                        <h3>Users & Permissions</h3>
                        <p>Control user access and administrative roles</p>
                    </div>
                )}

                {/* Only show System Logs to super admins */}
                {userIsSuperAdmin && (
                    <div className="action-card" onClick={() => navigate('/admin/logs')}>
                        <FaChartLine className="action-icon" />
                        <h3>System Logs</h3>
                        <p>View system activity and error logs</p>
                    </div>
                )}
                
                {/* Show Partnership Applications to admins and editors */}
                {canAccessApplications && (
                    <div className="action-card" onClick={() => navigate('/admin/partnership-applications')}>
                        <FaHandshake className="action-icon" />
                        <h3>Partnership Applications</h3>
                        <p>Review and manage partnership requests</p>
                    </div>
                )}
                
                {/* Show Academy Applications to everyone with access */}
                {canAccessApplications && (
                    <div className="action-card" onClick={() => navigate('/admin/academy-applications')}>
                        <FaGraduationCap className="action-icon" />
                        <h3>Academy Applications</h3>
                        <p>Process and manage academy enrollments</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminDashboard; 